"use client";

import type { Variants } from "motion/react";
import type { HTMLAttributes } from "react";

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { motion, useAnimation } from "motion/react";

interface RouteIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface RouteIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  loop?: boolean;
  animateOnMount?: boolean;
}

const pathVariants = (loop: boolean): Variants => ({
  normal: {
    pathLength: 1,
    opacity: 1,
  },
  animate: (custom: number) => ({
    pathLength: [0, 1],
    opacity: [0, 1],
    transition: {
      duration: 0.4,
      delay: 0.15 * custom,
      repeat: loop ? Number.POSITIVE_INFINITY : 0,
      repeatType: loop ? "loop" : undefined,
      repeatDelay: loop ? 1 : 0,
    },
  }),
});

const circleVariants = (): Variants => ({
  normal: {
    scale: 1,
    opacity: 1,
  },
  animate: (custom: number) => ({
    scale: [0.8, 1],
    opacity: [0, 1],
    transition: {
      duration: 0.3,
      delay: 0.15 * custom,
    },
  }),
});

const RouteIcon = forwardRef<RouteIconHandle, RouteIconProps>(
  (
    {
      onMouseEnter,
      onMouseLeave,
      className,
      size = 28,
      loop = false,
      animateOnMount = false,
      ...props
    },
    ref,
  ) => {
    const controls = useAnimation();
    const isControlledRef = useRef(false);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;

      return {
        startAnimation: () => void controls.start("animate"),
        stopAnimation: () => void controls.start("normal"),
      };
    });

    useEffect(() => {
      if ((loop || animateOnMount) && !isControlledRef.current) {
        void controls.start("animate");
      }
    }, [loop, animateOnMount, controls]);

    const handleMouseEnter = useCallback(
      async (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isControlledRef.current && !loop) {
          await controls.start("animate");
        } else {
          onMouseEnter?.(e);
        }
      },
      [controls, onMouseEnter, loop],
    );

    const handleMouseLeave = useCallback(
      async (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isControlledRef.current && !loop) {
          await controls.start("normal");
        } else {
          onMouseLeave?.(e);
        }
      },
      [controls, onMouseLeave, loop],
    );

    return (
      <div
        className={twMerge(className)}
        onMouseEnter={(e) => void handleMouseEnter(e)}
        onMouseLeave={(e) => void handleMouseLeave(e)}
        {...props}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <motion.circle
            cx="6"
            cy="19"
            r="3"
            initial="normal"
            variants={circleVariants()}
            animate={controls}
            custom={0}
          />
          <motion.path
            d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"
            initial="normal"
            variants={pathVariants(loop)}
            animate={controls}
            custom={1}
          />
          <motion.circle
            cx="18"
            cy="5"
            r="3"
            initial="normal"
            variants={circleVariants()}
            animate={controls}
            custom={2}
          />
        </svg>
      </div>
    );
  },
);

RouteIcon.displayName = "RouteIcon";

export { RouteIcon };
